import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import Logo from '../Logo';
import {
  Home, User, Bell, MessageCircle,
  Scissors, ShoppingBag,
} from 'lucide-react';

const ProfessionalLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, logout } = useAuth();
  const { unreadCount } = useNotifications();

  const desktopNavItems = [
    { path: '/professional-dashboard', label: 'Home' },
    { path: '/professional-dashboard/services', label: 'Services' },
    { path: '/professional-dashboard/products', label: 'Products' },
    { path: '/professional-dashboard/inbox', label: 'Inbox' },
  ];

  const mobileNavItems = [
    { path: '/professional-dashboard', icon: <Home size={22} /> },
    { path: '/professional-dashboard/services', icon: <Scissors size={22} /> },
    { path: '/professional-dashboard/products', icon: <ShoppingBag size={22} /> },
    { path: '/professional-dashboard/inbox', icon: <MessageCircle size={22} /> },
    { path: '/professional-dashboard/profile', icon: <User size={22} /> },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const goToNotifications = () => navigate('/professional-dashboard/notifications');

  const SOCIALS = [
    {
      label: 'Instagram',
      href: 'https://instagram.com/skytone.za',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4">
          <rect x="3" y="3" width="18" height="18" rx="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
        </svg>
      ),
    },
    {
      label: 'TikTok',
      href: 'https://tiktok.com/@skytone.za',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path d="M16.5 3c.3 2 1.6 3.4 3.5 3.7v2.6c-1.3.1-2.6-.3-3.6-1.1v6.1c0 3.3-2.7 5.7-5.8 5.7S4.8 17.6 4.8 14.3c0-3.2 2.6-5.7 5.8-5.7.4 0 .8 0 1.1.1v2.7c-.3-.1-.7-.2-1.1-.2-1.8 0-3.1 1.4-3.1 3.1 0 1.8 1.4 3.1 3.1 3.1s3.1-1.4 3.1-3.1V3h2.8z" />
        </svg>
      ),
    },
    {
      label: 'Facebook',
      href: 'https://facebook.com/skytone.za',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path d="M13.5 22v-8h2.7l.4-3.1h-3.1V8.9c0-.9.3-1.5 1.5-1.5h1.6V4.6c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.4-4 4.1v2.3H7.5V14h2.7v8h3.3z" />
        </svg>
      ),
    },
    {
      label: 'Pinterest',
      href: 'https://pinterest.com/skytone.za',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path d="M12 2C6.5 2 2 6.5 2 12c0 4.2 2.6 7.9 6.3 9.4-.1-.8-.2-2 0-2.9.2-.9 1.4-5.8 1.4-5.8s-.4-.7-.4-1.8c0-1.7 1-2.9 2.2-2.9 1 0 1.5.8 1.5 1.7 0 1-.6 2.5-1 3.9-.3 1.1.6 2.1 1.8 2.1 2.1 0 3.6-2.7 3.6-5.8 0-2.4-1.6-4.2-4.6-4.2-3.3 0-5.4 2.5-5.4 5.2 0 .9.3 1.5.7 2 .2.2.2.3.1.5-.1.2-.2.8-.2 1-.1.3-.3.4-.6.3-1.6-.7-2.4-2.5-2.4-4.5 0-3.3 2.8-7.3 8.4-7.3 4.5 0 7.5 3.3 7.5 6.8 0 4.6-2.6 8.1-6.5 8.1-1.3 0-2.5-.7-2.9-1.5 0 0-.7 2.8-.9 3.4-.3 1.1-1 2.4-1.6 3.3C9.9 21.8 10.9 22 12 22c5.5 0 10-4.5 10-10S17.5 2 12 2z" />
        </svg>
      ),
    },
  ];

  const FOOTER_LINKS = [
    {
      title: 'Business',
      links: [
        { label: 'Dashboard', path: '/professional-dashboard' },
        { label: 'Services', path: '/professional-dashboard/services' },
        { label: 'Products', path: '/professional-dashboard/products' },
        { label: 'Inbox', path: '/professional-dashboard/inbox' },
      ],
    },
    {
      title: 'Account',
      links: [
        { label: 'Profile', path: '/professional-dashboard/profile' },
        { label: 'Notifications', path: '/professional-dashboard/notifications' },
      ],
    },
    {
      title: 'Clients',
      links: [
        { label: 'Discover', path: '/dashboard/search' },
        { label: 'Switch to Client', path: '/dashboard' },
      ],
    },
  ];

  return (
    <>
      <div className="min-h-screen bg-[#FCF8F9] flex flex-col">
        {/* ============================================================ */}
        {/* DESKTOP NAVBAR                                               */}
        {/* ============================================================ */}
        <div className="hidden md:flex justify-center pt-4 px-4 pointer-events-none">
          <header className="pointer-events-auto w-full max-w-6xl bg-white/70 backdrop-blur-xl border border-white/30 shadow-lg rounded-full px-6 py-2 transition-all duration-300">
            <div className="flex items-center justify-between h-14">
              <div className="cursor-pointer shrink-0" onClick={() => navigate('/professional-dashboard')}>
                <Logo size={100} className="text-[#CDBCE7]" />
              </div>

              <nav className="flex items-center gap-1 flex-1 justify-center">
                {desktopNavItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`px-4 py-2 rounded-full transition-all duration-300 text-sm font-medium ${
                      isActive(item.path)
                        ? 'bg-[#FCF8F9] text-[#CDBCE7] shadow-sm'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-white/30'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>

              <div className="flex items-center gap-1 shrink-0">
                {/* Bell → full notifications page */}
                <button
                  onClick={goToNotifications}
                  className={`relative p-2 rounded-full transition-colors ${
                    isActive('/professional-dashboard/notifications')
                      ? 'bg-[#FCF8F9] text-[#CDBCE7]'
                      : 'hover:bg-white/50 text-gray-600'
                  }`}
                  title="Notifications"
                >
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-400 text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => navigate('/professional-dashboard/profile')}
                  className="flex items-center gap-2 pl-2 border-l border-gray-200/50"
                >
                  <div className="w-8 h-8 rounded-full bg-[#FCF8F9] flex items-center justify-center text-[#CDBCE7] overflow-hidden">
                    {userData?.profileImage ? (
                      <img src={userData.profileImage} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User size={16} />
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {userData?.name || 'Professional'}
                  </span>
                </button>

                <button
                  onClick={handleLogout}
                  className="text-sm text-gray-400 hover:text-red-500 transition-colors"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </header>
        </div>

        {/* ============================================================ */}
        {/* MOBILE HEADER — bell → full notifications page                */}
        {/* ============================================================ */}
        <header className="md:hidden sticky top-0 z-50 bg-[#FCF8F9]/80 backdrop-blur-xl px-6 pt-10 pb-6">
          <div className="flex justify-between items-center">
            <div className="w-full flex justify-center">
              <div className="cursor-pointer" onClick={() => navigate('/professional-dashboard')}>
                <Logo size={100} className="text-[#CDBCE7]" />
              </div>
            </div>
            <div className="absolute right-6 flex items-center gap-2">
              <button
                onClick={goToNotifications}
                className="relative w-11 h-11 bg-[#FCF8F9] text-[#CDBCE7] rounded-2xl flex items-center justify-center hover:bg-[#EDE4F5] transition-colors shadow-sm shrink-0"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-400 text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 lg:px-8 py-6 pb-32 lg:pb-6 w-full flex-1">
          {children}
        </main>

        {/* ============================================================ */}
        {/* DESKTOP-ONLY FOOTER                                          */}
        {/* ============================================================ */}
        <footer className="hidden md:block bg-white border-t border-gray-100 mt-12">
          <div className="max-w-6xl mx-auto px-8 py-12">
            <div className="grid grid-cols-12 gap-8">
              <div className="col-span-5">
                <div className="cursor-pointer inline-block mb-4" onClick={() => navigate('/professional-dashboard')}>
                  <Logo size={120} className="text-[#CDBCE7]" />
                </div>
                <p className="text-sm text-gray-500 max-w-xs leading-relaxed mb-6">
                  Grow your beauty business with Skytone. Manage bookings, products and clients — all in one place.
                </p>
                <div className="flex items-center gap-2">
                  {SOCIALS.map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={s.label}
                      aria-label={s.label}
                      className="w-9 h-9 rounded-full bg-[#F4EEFF] text-[#AF8BD4] flex items-center justify-center hover:bg-[#E4D9F7] hover:scale-105 transition-all"
                    >
                      {s.icon}
                    </a>
                  ))}
                </div>
              </div>

              {FOOTER_LINKS.map((col) => (
                <div key={col.title} className="col-span-2">
                  <h3 className="text-[11px] font-bold text-gray-900 uppercase tracking-[0.15em] mb-4">
                    {col.title}
                  </h3>
                  <ul className="space-y-2.5">
                    {col.links.map((link) => (
                      <li key={link.label}>
                        <button
                          type="button"
                          onClick={() => navigate(link.path)}
                          className="text-sm text-gray-500 hover:text-[#AF8BD4] transition-colors text-left"
                        >
                          {link.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              <div className="col-span-1" />
            </div>

            <div className="mt-12 pt-6 border-t border-gray-100 flex items-center justify-between">
              <p className="text-xs text-gray-400">
                © {new Date().getFullYear()} Skytone. All rights reserved.
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="https://instagram.com/skytone.za"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-400 hover:text-[#AF8BD4] transition-colors"
                >
                  @skytone.za
                </a>
                <span className="text-gray-200">·</span>
                <span className="text-xs text-gray-400">Made with care</span>
              </div>
            </div>
          </div>
        </footer>

        {/* ============================================================ */}
        {/* MOBILE BOTTOM NAV                                            */}
        {/* ============================================================ */}
        <nav className="md:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-[340px]">
          <div className="bg-white/60 backdrop-blur-xl border border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.12)] rounded-full p-2 flex justify-between items-center px-4">
            {mobileNavItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`p-2.5 rounded-full transition-all duration-300 ${
                  isActive(item.path)
                    ? 'bg-[#FCF8F9] text-[#CDBCE7] shadow-sm'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-white/30'
                }`}
              >
                {item.icon}
              </button>
            ))}
          </div>
        </nav>
      </div>
    </>
  );
};

export default ProfessionalLayout;